import { HttpErrorResponse, HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ROUTE_NAMES } from '@app/core/navigation/navigation.constants';
import { PopupService } from '@app/core/services/popup.service';
import { ProfileState } from '@app/layout/common/profile/state/profile.state';
import { environment } from '@environments/environment';
import * as Sentry from '@sentry/angular';
import { handleResponseError } from '@shared/utils/helper';
import { BehaviorSubject, catchError, filter, finalize, retry, shareReplay, switchMap, take, tap, throwError, timer } from 'rxjs';
import { AuthService } from './auth.service';

let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);
const RETRIED = 'x-retired-after-refresh';
let cookieSessionValue: string | null = null;

const attach = (req: HttpRequest<any>, token?: string) => {
  const headers: Record<string, string> = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (cookieSessionValue) {
    headers['Cookie-Session'] = cookieSessionValue;
  }
  return Object.keys(headers).length ? req.clone({ setHeaders: headers }) : req;
};
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const router: Router = inject(Router);
  const profileState: ProfileState = inject(ProfileState);
  const matDialog: MatDialog = inject(MatDialog);
  const popupService: PopupService = inject(PopupService);

  const token = req.headers.has('skipAuth') ? null : auth.accessToken;
  req = attach(req, token);

  const signOut = () => {
    auth.clearInformation();
    matDialog.closeAll();
    setTimeout(() => {
      router.navigate([`${ROUTE_NAMES.SIGN_IN}`]).then();
    }, 1000);
  };

  // Create shared observable to prevent duplicate requests
  return next(req).pipe(
    tap((event) => {
      if (event instanceof HttpResponse) {
        // try to read from response headers first
        const headerCookie = event.headers.get('Cookie-Session');
        // fallback to request headers (may be absent in browsers) or document.cookie
        const reqHeaderCookie = req.headers.get('Cookie-Session') ?? null;
        const docCookie = localStorage.getItem('Cookie-Session');

        const cookieSession = headerCookie ?? reqHeaderCookie ?? docCookie;
        if (cookieSession) {
          cookieSessionValue = cookieSession;
        }
      }
    }),
    catchError((err: HttpErrorResponse) => {
      if (err.status !== 401 || req.headers.has(RETRIED)) {
        if (err && !err.url.includes('/auth/logout') && !req.headers.has('Private-Image') && !err.url.includes('notifcations')) {
          popupService.messageSnackbar({
            type: 'error',
            icon: 'heroicons_outline:exclamation-circle',
            message: handleResponseError(err)
          });
        }
        popupService.closeLoadingDialog();
        return throwError(() => err);
      }

      const scope = Sentry.getCurrentScope();

      scope.setTag('service', environment.SERVICE_ID);

      scope.setUser({
        username: (profileState?.firstName() || 'first_name') + ' ' + (profileState?.lastName() || 'last_name'),
        email: profileState.profile()?.email || '',
        role: profileState.profile()?.role_name || ''
      });

      scope.setExtra('Payload', {
        url: req.url,
        method: req.method,
        requestData: req.body,
        status: err.status,
        statusText: err.statusText
      });

      if (isRefreshing) {
        return refreshTokenSubject.pipe(
          filter((t): t is string => t != null),
          take(1),
          switchMap((t) =>
            next(attach(req.clone({ setHeaders: { [RETRIED]: '1' } }), t)).pipe(
              retry({
                count: 2,
                delay: (error, retryCount) => delay(error, retryCount)
              })
            )
          )
        );
      }

      isRefreshing = true;
      refreshTokenSubject.next(null);

      return auth.refreshTOKEN().pipe(
        switchMap((res: any) => {
          const newAccess = res?.access_token;
          const newRefresh = res?.refresh_token;
          if (!newAccess) {
            return throwError(() => new Error('No access_token in response'));
          }
          auth.accessToken = newAccess;
          auth.refreshToken = newRefresh;
          refreshTokenSubject.next(newAccess);

          const retried = attach(req.clone({ setHeaders: { [RETRIED]: '1' } }), newAccess);

          return next(retried).pipe(
            retry({
              count: 2,
              delay: (error, retryCount) => delay(error, retryCount)
            })
          );
        }),
        catchError((refreshErr) => {
          popupService.messageSnackbar(
            {
              icon: 'heroicons_outline:no-symbol',
              message: 'Session Expired.',
              type: 'info'
            },
            4000
          );
          signOut();
          return throwError(() => refreshErr);
        }),
        finalize(() => {
          isRefreshing = false;
        })
      );
    }),
    // Share replay to prevent duplicate subscriptions
    shareReplay({ bufferSize: 1, refCount: true })
  );
};

const delay = (error: HttpErrorResponse, retryCount: number) => {
  if ([500, 502, 503, 504].includes(error.status)) {
    return timer(500 * Math.pow(2, retryCount - 1));
  }
  if (error.status === 401 && retryCount === 1) {
    return timer(300);
  }
  return throwError(() => error);
};
